import type {
  InsertApprovalMutation,
  InsertApprovalMutationVariables,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

import { mapInsertApprovalVariables } from './approvalInputMapping';

/**
 * Maps the tRPC mutation response to match the GraphQL mutation structure so
 * consumers can branch on success without knowing which path ran.
 */
const mapTrpcResponseToGraphQL = (trpcData: {
  Id: string;
}): InsertApprovalMutation => ({
  insert_approval_one: {
    __typename: 'approval',
    Id: trpcData.Id,
  },
});

export const useInsertApprovalTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.approval.insert.mutationOptions({
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.approval.globalApprovals.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.approval.approvalLevels.queryKey(),
          }),
        ]);
      },
    }),
  });

  return {
    insertApproval: async (
      approval: InsertApprovalMutationVariables['approval']
    ): Promise<InsertApprovalMutation> => {
      const result = await mutation.mutateAsync(
        mapInsertApprovalVariables(approval)
      );

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
